import { api } from "api";
import { delay } from "@vyne-shared/utils/delay";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

/**
 * Client hook to query for existing Patients/Responsible Parties when entering a new payment request.
 * The search is not actually performed if the supplied filter is not valid (too short).
 * Optionally, the search is debounced, by waiting the prescribed amount of time before searching,
 * and canceling any previous requests as applicable.
 * @param searchFilter Required filter string to perform the query.
 * @param excludeUnlinked If set, P/RPs not linked to PMS patients are not included.
 * @param debounceDelay Number of milliseconds to wait before actually querying. 0 to disable debounce.
 * @returns
 *  - React Query properties
 *  - isFilterValid: boolean: conveys whether or not the supplied search filter was valid enough to
 *    actually perform the search.
 */
export function usePatientQuery(searchFilter: string, excludeUnlinked: boolean, debounceDelay: number) {
    const isFilterValid = searchFilter.length >= 3;

    const { isFetching, data } = useQuery({
        queryKey: ["searchPatientField", searchFilter, isFilterValid, excludeUnlinked, debounceDelay],
        queryFn: async (context) => {
            // bail if filter invalid or debounce delay interrupted due to cancellation
            if (!isFilterValid || (await delay(debounceDelay, context.signal))) {
                return null;
            }

            return await api.provider.queryPatients(searchFilter, excludeUnlinked, context.signal);
        },
        placeholderData: keepPreviousData,
    });

    return { isFilterValid, isFetching, data } as const;
}
